import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Pin, PinOff, Search, X } from "lucide-react";
import { useI18n } from "../../i18n";

export const isActiveItem = (m, loc) => {
  if (!m.to) return false;
  const [path, query] = m.to.split("?");
  const want = new URLSearchParams(query || "");
  const have = new URLSearchParams(loc.search);
  if (query) return loc.pathname === path && ["tab", "focus", "section"].every((k) => want.get(k) === have.get(k));
  return (loc.pathname === path && !["tab", "section"].some((k) => have.get(k))) || loc.pathname.startsWith(`${path}/`);
};

const Badge = ({ n, id }) => (n > 0 ? <span data-testid={id} className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-[11px] font-bold text-navy">{n > 99 ? "99+" : n}</span> : null);

const NavItem = ({ m, badge, pinned, onPin, onGo, onAction, prefix }) => {
  const { t } = useI18n();
  const loc = useLocation();
  const active = isActiveItem(m, loc);
  const Icon = m.icon;
  const cls = `group flex h-10 min-w-0 flex-1 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors duration-150 ${active ? "bg-white/12 text-white" : "text-white/65 hover:bg-white/5 hover:text-white"}`;
  const body = <><Icon className={`h-[18px] w-[18px] shrink-0 ${active ? "text-brand" : ""}`} /><span className="truncate">{t(`snav.item.${m.key}`)}</span><Badge n={badge} id={`staff-badge-${m.key}`} /></>;
  return (
    <div className="group/item flex min-w-0 items-center" title={t(`snav.item.${m.key}`)}>
      {m.to
        ? <Link to={m.to} onClick={() => onGo(m)} className={cls} aria-current={active ? "page" : undefined} data-testid={`${prefix}-${m.key}`}>{body}</Link>
        : <button type="button" onClick={() => onAction(m.action)} className={cls} data-testid={`${prefix}-${m.key}`}>{body}</button>}
      {m.to && (
        <button type="button" onClick={() => onPin(m.key)} aria-label={t(pinned ? "snav.unpin" : "snav.pin")} data-testid={`${prefix}-pin-${m.key}`}
          className={`grid h-9 w-8 shrink-0 place-items-center rounded-lg text-white/40 transition-opacity hover:text-brand ${pinned ? "opacity-100" : "opacity-100 lg:opacity-0 lg:group-hover/item:opacity-100"}`}>
          {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
        </button>
      )}
    </div>
  );
};

const Group = ({ g, open, onToggle, badgeOf, children }) => {
  const { t } = useI18n();
  const total = g.items.reduce((s, m) => s + (badgeOf(m) || 0), 0);
  const Icon = g.icon;
  return (
    <div data-testid={`staff-group-${g.key}`}>
      <button type="button" onClick={() => onToggle(g.key)} aria-expanded={open} data-testid={`staff-group-toggle-${g.key}`}
        className="flex h-9 w-full items-center gap-2 rounded-lg px-3 text-[11px] font-semibold uppercase tracking-wider text-white/45 transition-colors hover:text-white/80">
        <Icon className="h-3.5 w-3.5" /><span className="truncate">{t(`snav.group.${g.key}`)}</span>
        {!open && <Badge n={total} id={`staff-group-badge-${g.key}`} />}
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${open ? "" : "-rotate-90"} ${!open && total ? "" : "ml-auto"}`} />
      </button>
      {open && <div className="grid grid-cols-1 gap-0.5 pb-2">{children}</div>}
    </div>
  );
};

export const StaffNav = ({ groups, badgeOf, prefs, onPrefs, onAction, onGo, prefix = "staff-menu" }) => {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const all = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const loc = useLocation();
  const openSet = new Set(groups.map((g) => g.key).filter((k) => !(prefs.closed || []).includes(k)));
  const activeGroup = groups.find((g) => g.items.some((m) => isActiveItem(m, loc)));
  if (activeGroup) openSet.add(activeGroup.key);
  const pinned = all.filter((m) => prefs.pinned.includes(m.key));
  const toggle = (k) => onPrefs({ closed: openSet.has(k) ? [...(prefs.closed || []), k] : (prefs.closed || []).filter((x) => x !== k) });
  const pin = (k) => onPrefs({ pinned: prefs.pinned.includes(k) ? prefs.pinned.filter((x) => x !== k) : [...prefs.pinned, k] });
  const row = (m) => <NavItem key={m.key} m={m} badge={badgeOf(m)} pinned={prefs.pinned.includes(m.key)} onPin={pin} onGo={onGo} onAction={onAction} prefix={prefix} />;
  const needle = q.trim().toLocaleLowerCase("el").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const norm = (s) => s.toLocaleLowerCase("el").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const hits = needle ? all.filter((m) => norm(`${t(`snav.item.${m.key}`)} ${t(`snav.group.${groups.find((g) => g.items.includes(m)).key}`)}`).includes(needle)) : [];
  return (
    <div className="flex flex-col gap-1" data-testid={`${prefix}-nav`}>
      <label className="relative mb-2 block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("snav.search")} data-testid={`${prefix}-search`}
          className="h-10 w-full rounded-xl border border-white/10 bg-white/5 pl-9 pr-8 text-sm text-white placeholder:text-white/40 focus:border-brand/60 focus:outline-none" />
        {q && <button type="button" onClick={() => setQ("")} aria-label="clear" className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center text-white/50"><X className="h-4 w-4" /></button>}
      </label>
      {needle ? (
        <div className="grid gap-0.5" data-testid={`${prefix}-results`}>{hits.map(row)}{!hits.length && <p className="px-3 py-2 text-sm text-white/50">{t("snav.noHits")}</p>}</div>
      ) : (
        <>
          {pinned.length > 0 && (
            <div className="mb-1 border-b border-white/10 pb-2" data-testid={`${prefix}-pinned`}>
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-brand/80">{t("snav.pinned")}</p>
              <div className="grid gap-0.5">{pinned.map(row)}</div>
            </div>
          )}
          {groups.map((g) => (g.standalone ? <div key={g.key} className="grid grid-cols-1 pb-1" data-testid={`staff-group-${g.key}`}>{g.items.map(row)}</div> : (
            <Group key={g.key} g={g} open={openSet.has(g.key)} onToggle={toggle} badgeOf={badgeOf}>{g.items.map(row)}</Group>
          )))}
        </>
      )}
    </div>
  );
};
